import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/*
 * Proactive deploy detector. Polls the build manifest and, when a NEW build is
 * live, shows a non-blocking "Update available — Refresh" toast so the user
 * reloads on their own terms BEFORE they hit a missing chunk. This turns the
 * jarring post-deploy error page into a calm, intentional update prompt.
 *
 * Signature = the hashed main.js path from CRA's asset-manifest.json, which
 * changes on every build. The manifest is served `no-cache`, and we add a
 * cache-buster + `no-store` so the poll always sees the live value.
 *
 * Two things this deliberately does NOT do, because the cache-buster makes every
 * tick a full uncacheable download of the whole manifest:
 *
 *   - It does not poll a hidden tab. A parked dashboard used to pull the
 *     manifest 20 times an hour forever. The visibilitychange handler below
 *     already re-checks the moment the tab comes back, so a backgrounded tab
 *     learns about a deploy at exactly the same time it used to — when the user
 *     looks at it — for none of the traffic.
 *   - It does not keep polling after the toast is up. Once the user has been
 *     told, every further request can only re-discover the same answer.
 */
const MANIFEST_URL = '/asset-manifest.json';
const POLL_MS = 3 * 60 * 1000; // 3 minutes

async function fetchBuildSignature() {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = await res.json();
    return (json && json.files && json.files['main.js']) || null;
  } catch (e) {
    // Network blip / offline — ignore this tick; we'll try again next poll.
    return null;
  }
}

export default function VersionUpdateNotifier() {
  const { t } = useTranslation();
  const bootSignature = useRef(null);
  const notified = useRef(false);

  useEffect(() => {
    let alive = true;

    // `force` is for the mount read only: a tab opened in the BACKGROUND still
    // has to record its boot signature, or it has no baseline to compare
    // against later and the notifier is silently dead for that tab's whole
    // life. Routing that first read through check() rather than a bare fetch
    // also keeps the original compare semantics — if a visibilitychange read
    // lands first and the mount read arrives late, the mismatch is still
    // noticed instead of being dropped.
    const check = async (force = false) => {
      // Nothing to learn while the tab is hidden or once the toast is showing.
      if (notified.current || (!force && document.visibilityState === 'hidden')) return;
      const sig = await fetchBuildSignature();
      if (!alive || !sig) return;
      if (bootSignature.current === null) {
        // First successful read = the build this tab is running.
        bootSignature.current = sig;
        return;
      }
      if (sig !== bootSignature.current && !notified.current) {
        notified.current = true;
        toast(
          () => (
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>{t('cm.update.available')}</span>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('cm.update.refresh')}
              </button>
            </span>
          ),
          { id: 'app-update', duration: Infinity }
        );
      }
    };

    check(true);
    const interval = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [t]);

  return null;
}
